import React, { useEffect } from "react";
import { X } from "lucide-react";
import VaultCard from "./VaultCard";

const RARITY_COLOR = {
  common: "#94a3b8",
  uncommon: "#00ff88",
  rare: "#22d3ee",
  mythic: "#a78bfa",
  classified: "#ff3b30",
  divine: "#ffd700",
  celestial: "#d946ef",
};

/**
 * VaultCardDetailModal — full-screen viewer for any owned/revealed card.
 * Triggered by clicking a card in the collection or trade lists.
 *
 * Layout (desktop): card on the left, metadata column on the right.
 * Mobile: stacked vertically with the card on top.
 *
 * Closes on Escape, backdrop click, or X button.
 */
export default function VaultCardDetailModal({ card, editionNumber, copies, onClose }) {
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
    window.addEventListener("keydown", onKey);
    // Lock body scroll while open
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [onClose]);

  if (!card) return null;
  const rarity = card.rarity || "common";
  const color = RARITY_COLOR[rarity] || "#94a3b8";

  return (
    <div
      data-testid="vault-card-detail-modal"
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-[300] bg-[#050505]/95 backdrop-blur-md overflow-y-auto"
      onClick={onClose}
    >
      <div className="min-h-full flex items-start sm:items-center justify-center p-4 sm:p-8">
        <div
          className="relative w-full max-w-3xl bg-[#0a0a0a] border shadow-[0_0_80px_-20px_rgba(255,255,255,0.15)]"
          style={{ borderColor: `${color}55` }}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="h-[3px] w-full" style={{ backgroundColor: color }} />

          <button
            type="button"
            onClick={onClose}
            data-testid="vault-card-detail-close"
            className="absolute top-3 right-3 z-10 text-zinc-400 hover:text-white bg-[#050505]/70 border border-white/10 p-1.5 transition-colors"
            aria-label="Close"
          >
            <X size={16} />
          </button>

          <div className="grid sm:grid-cols-[260px_1fr] gap-6 sm:gap-8 p-6 sm:p-8">
            {/* Card — passed clickable={false} so clicking it inside the modal
                doesn't re-open another modal. */}
            <div className="flex justify-center">
              <VaultCard
                card={card}
                editionNumber={editionNumber}
                copies={copies}
                variant="revealed"
                size="lg"
                clickable={false}
              />
            </div>

            {/* Metadata column */}
            <div className="flex flex-col">
              <div className="font-mono text-[10px] tracking-[0.3em] mb-2" style={{ color }}>
                // {(card.subject_type || "card").toUpperCase()} · {rarity.toUpperCase()}
              </div>
              <h2
                data-testid="vault-card-detail-name"
                className="font-heading font-black uppercase tracking-tight text-3xl sm:text-4xl text-white leading-[0.95]"
              >
                {card.name}
              </h2>

              {card.flavor && (
                <p className="mt-4 text-sm text-zinc-300 leading-relaxed italic">
                  "{card.flavor}"
                </p>
              )}

              {/* Stats block */}
              <div className="mt-6 grid grid-cols-3 gap-3">
                <StatBlock label="CREDIBILITY" value={card.stats?.credibility} color={color} />
                <StatBlock label="IMPACT" value={card.stats?.impact} color={color} />
                <StatBlock label="DISTURBANCE" value={card.stats?.disturbance} color={color} />
              </div>

              {/* Copy info */}
              <div className="mt-6 border border-white/10 p-4 space-y-2">
                <Row label="COPY">
                  {editionNumber != null ? (
                    <>#{editionNumber}{card.edition_limit ? ` of ${card.edition_limit}` : ""}</>
                  ) : "—"}
                </Row>
                <Row label="OWNED">
                  {copies ? `×${copies} ${copies === 1 ? "copy" : "copies"}` : "Not in collection"}
                </Row>
                {card.set_id && <Row label="SET">{String(card.set_id).toUpperCase()}</Row>}
                {card.released_at && (
                  <Row label="RELEASED">
                    {new Date(card.released_at).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" })}
                  </Row>
                )}
              </div>

              {/* Dossier link removed — Vault Cards stand on their own
                  inside the detail modal; clicking through to a dossier
                  was inconsistent (many cards have no matching slug) and
                  looked out of place when it appeared. */}
            </div>
          </div>

          <div className="h-[3px] w-full" style={{ backgroundColor: color }} />
        </div>
      </div>
    </div>
  );
}

function StatBlock({ label, value, color }) {
  return (
    <div className="border border-white/10 p-3 text-center">
      <div className="font-mono text-[9px] tracking-[0.2em] text-zinc-500 mb-1">{label}</div>
      <div className="font-heading text-3xl font-black" style={{ color }}>
        {value ?? "—"}
      </div>
    </div>
  );
}

function Row({ label, children }) {
  return (
    <div className="flex items-center justify-between gap-3 font-mono text-[10px] tracking-[0.2em]">
      <span className="text-zinc-500">{label}</span>
      <span className="text-zinc-200 text-right">{children}</span>
    </div>
  );
}
